import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createLanguageLesson,
  createModule,
  createTopic,
  deleteLanguageLesson,
  deleteModule,
  deleteTopic,
  getCourseById,
  getLanguages,
  getLanguageLessons,
  getModules,
  getTopics,
  updateCourse,
  updateLanguageLesson,
  updateModule,
  updateTopic,
} from "../api";
import "./createcourse.css";

const EMPTY_LESSON = {
  language_id: "",
  topic_content: "",
  topic_image: null,
  audio: null,
  video: null,
  document: null,
};

const CourseEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [activeSection, setActiveSection] = useState("overview");

  // ── Overview (full tree) ─────────────────────────────────────────────────
  const [tree, setTree]           = useState([]); // [{module, topics:[{topic, lessons:[]}]}]
  const [treeLoading, setTreeLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  // ── Course ──────────────────────────────────────────────────────────────
  const [courseDraft, setCourseDraft] = useState({
    title: "", description: "", visibility: "public", image: null,
  });

  // ── Modules ─────────────────────────────────────────────────────────────
  const [modules, setModules]               = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [moduleDraft, setModuleDraft]       = useState({ title: "", introduction: "" });

  // ── Topics ───────────────────────────────────────────────────────────────
  const [topics, setTopics]               = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicDraft, setTopicDraft]       = useState({ title: "", description: "" });

  // ── Lessons ──────────────────────────────────────────────────────────────
  const [lessons, setLessons]           = useState([]);
  const [languages, setLanguages]       = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null); // null = new lesson mode
  const [lessonDraft, setLessonDraft]   = useState(EMPTY_LESSON);

  // ── Derived ──────────────────────────────────────────────────────────────
  const selectedModule = useMemo(
    () => modules.find((m) => m.id === selectedModuleId) || null,
    [modules, selectedModuleId],
  );
  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) || null,
    [topics, selectedTopicId],
  );

  const langName = (langId) =>
    languages.find((l) => String(l.id) === String(langId))?.name ||
    (langId ? `Language #${langId}` : "—");

  // ── Flash ─────────────────────────────────────────────────────────────────
  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };
  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 5000);
  };

  // ── Refresh helpers ──────────────────────────────────────────────────────
  const refreshModules = async () => {
    const items = await getModules(id);
    setModules(items);
    return items;
  };

  const refreshTopics = async (moduleId) => {
    if (!moduleId) { setTopics([]); return []; }
    const items = await getTopics(moduleId);
    setTopics(items);
    return items;
  };

  const refreshLessons = async (topicId) => {
    if (!topicId) { setLessons([]); return; }
    const items = await getLanguageLessons(topicId);
    setLessons(Array.isArray(items) ? items : []);
  };

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const [course, langItems, moduleItems] = await Promise.all([
          getCourseById(id),
          getLanguages(),
          getModules(id),
        ]);

        setLanguages(langItems);
        setCourseDraft({
          title:       course?.title || "",
          description: course?.description || "",
          visibility:  course?.is_published ? "public" : "private",
          image:       course?.image || null,
        });
        setModules(moduleItems);

        if (moduleItems.length > 0) {
          const firstMod = moduleItems[0];
          setSelectedModuleId(firstMod.id);
          const topicItems = await getTopics(firstMod.id);
          setTopics(topicItems);
          if (topicItems.length > 0) {
            setSelectedTopicId(topicItems[0].id);
            const lessonItems = await getLanguageLessons(topicItems[0].id);
            setLessons(Array.isArray(lessonItems) ? lessonItems : []);
          }
        }
      } catch (err) {
        setError(err?.message || "Failed to load course.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // ── When module selector changes (Topics / Lessons tabs) ─────────────────
  const handleModuleChange = async (moduleId) => {
    setSelectedModuleId(moduleId ? Number(moduleId) : null);
    setSelectedTopicId(null);
    setTopics([]);
    setLessons([]);
    setSelectedLessonId(null);
    setLessonDraft(EMPTY_LESSON);
    if (moduleId) {
      const items = await refreshTopics(Number(moduleId));
      if (items.length > 0) setSelectedTopicId(items[0].id);
    }
  };

  const handleTopicChange = async (topicId) => {
    setSelectedTopicId(topicId ? Number(topicId) : null);
    setLessons([]);
    setSelectedLessonId(null);
    setLessonDraft(EMPTY_LESSON);
    if (topicId) await refreshLessons(Number(topicId));
  };

  // =========================================================================
  // COURSE
  // =========================================================================
  const buildCoursePayload = () => {
    if (courseDraft.image instanceof File) {
      const fd = new FormData();
      fd.append("title",        courseDraft.title.trim());
      fd.append("description",  (courseDraft.description || "").trim());
      fd.append("is_published", courseDraft.visibility === "public" ? "1" : "0");
      fd.append("image_file",   courseDraft.image);
      return fd;
    }
    return {
      title:        courseDraft.title.trim(),
      description:  (courseDraft.description || "").trim(),
      is_published: courseDraft.visibility === "public",
    };
  };

  const handleSaveCourse = async () => {
    if (!courseDraft.title.trim()) { showError("Course title is required."); return; }
    try {
      setSaving(true); setError("");
      await updateCourse(id, buildCoursePayload());
      flash("Course updated successfully.");
    } catch (err) {
      showError(err?.message || "Failed to update course.");
    } finally { setSaving(false); }
  };

  // =========================================================================
  // MODULES
  // =========================================================================
  const handleAddModule = async () => {
    if (!moduleDraft.title.trim()) { showError("Module title is required."); return; }
    try {
      setSaving(true); setError("");
      await createModule({
        course_id:    id,
        title:        moduleDraft.title.trim(),
        introduction: (moduleDraft.introduction || "").trim(),
      });
      setModuleDraft({ title: "", introduction: "" });
      const items = await refreshModules();
      const added = items[items.length - 1];
      if (added) {
        setSelectedModuleId(added.id);
        await refreshTopics(added.id);
      }
      flash("Module added.");
    } catch (err) {
      showError(err?.message || "Failed to add module.");
    } finally { setSaving(false); }
  };

  const handleUpdateModule = async () => {
    if (!selectedModuleId || !selectedModule) return;
    try {
      setSaving(true); setError("");
      await updateModule(selectedModuleId, {
        title:        selectedModule.title || "",
        introduction: selectedModule.introduction || "",
      });
      await refreshModules();
      flash("Module updated.");
    } catch (err) {
      showError(err?.message || "Failed to update module.");
    } finally { setSaving(false); }
  };

  const handleDeleteModule = async () => {
    if (!selectedModuleId) return;
    if (!window.confirm("Delete this module and all its topics and lessons?")) return;
    try {
      setSaving(true); setError("");
      await deleteModule(selectedModuleId);
      const items = await refreshModules();
      const next = items[0] || null;
      setSelectedModuleId(next?.id || null);
      setTopics([]);
      setSelectedTopicId(null);
      setLessons([]);
      if (next) await refreshTopics(next.id);
      flash("Module deleted.");
    } catch (err) {
      showError(err?.message || "Failed to delete module.");
    } finally { setSaving(false); }
  };

  // =========================================================================
  // TOPICS
  // =========================================================================
  const handleAddTopic = async () => {
    if (!selectedModuleId) { showError("Select a module first."); return; }
    if (!topicDraft.title.trim()) { showError("Topic title is required."); return; }
    try {
      setSaving(true); setError("");
      await createTopic({
        module_id:   selectedModuleId,
        title:       topicDraft.title.trim(),
        description: (topicDraft.description || "").trim(),
      });
      setTopicDraft({ title: "", description: "" });
      const items = await refreshTopics(selectedModuleId);
      const added = items[items.length - 1];
      if (added) {
        setSelectedTopicId(added.id);
        await refreshLessons(added.id);
      }
      flash("Topic added.");
    } catch (err) {
      showError(err?.message || "Failed to add topic.");
    } finally { setSaving(false); }
  };

  const handleUpdateTopic = async () => {
    if (!selectedTopicId || !selectedTopic) return;
    try {
      setSaving(true); setError("");
      await updateTopic(selectedTopicId, {
        title:       selectedTopic.title || "",
        description: selectedTopic.description || "",
      });
      await refreshTopics(selectedModuleId);
      flash("Topic updated.");
    } catch (err) {
      showError(err?.message || "Failed to update topic.");
    } finally { setSaving(false); }
  };

  const handleDeleteTopic = async () => {
    if (!selectedTopicId) return;
    if (!window.confirm("Delete this topic and all its lessons?")) return;
    try {
      setSaving(true); setError("");
      await deleteTopic(selectedTopicId);
      const items = await refreshTopics(selectedModuleId);
      const next = items[0] || null;
      setSelectedTopicId(next?.id || null);
      setLessons([]);
      if (next) await refreshLessons(next.id);
      flash("Topic deleted.");
    } catch (err) {
      showError(err?.message || "Failed to delete topic.");
    } finally { setSaving(false); }
  };

  // =========================================================================
  // LESSONS
  // =========================================================================
  const buildLessonPayload = () => {
    const fd = new FormData();
    fd.append("topic_id",      String(selectedTopicId));
    fd.append("topic_name",    selectedTopic?.title || "Untitled Topic");
    fd.append("language_id",   String(lessonDraft.language_id));
    fd.append("topic_content", lessonDraft.topic_content || "");
    if (lessonDraft.topic_image instanceof File) fd.append("topic_image", lessonDraft.topic_image);
    if (lessonDraft.audio      instanceof File) fd.append("audio",        lessonDraft.audio);
    if (lessonDraft.video      instanceof File) fd.append("video",        lessonDraft.video);
    if (lessonDraft.document   instanceof File) fd.append("document",     lessonDraft.document);
    return fd;
  };

  const handleSaveLesson = async () => {
    if (!selectedTopicId)         { showError("Select a topic first."); return; }
    if (!lessonDraft.language_id) { showError("Please select a language."); return; }
    try {
      setSaving(true); setError("");
      if (selectedLessonId) {
        await updateLanguageLesson(selectedLessonId, buildLessonPayload());
        flash("Lesson updated.");
      } else {
        await createLanguageLesson(buildLessonPayload());
        flash("Lesson added.");
      }
      setLessonDraft(EMPTY_LESSON);
      setSelectedLessonId(null);
      await refreshLessons(selectedTopicId);
    } catch (err) {
      showError(err?.message || "Failed to save lesson.");
    } finally { setSaving(false); }
  };

  const handleEditLesson = (lesson) => {
    setSelectedLessonId(lesson.id);
    setLessonDraft({
      language_id:   lesson.language_id ? String(lesson.language_id) : "",
      topic_content: lesson.topic_content || "",
      topic_image:   null,
      audio:         null,
      video:         null,
      document:      null,
      _existing: {
        image_url:    lesson.image_url    || null,
        audio_file:   lesson.audio        || null,
        video_url:    lesson.video_url    || null,
        document_url: lesson.document_url || null,
      },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelLesson = () => {
    setSelectedLessonId(null);
    setLessonDraft(EMPTY_LESSON);
  };

  // =========================================================================
  // OVERVIEW TREE
  // =========================================================================
  const loadTree = async () => {
    try {
      setTreeLoading(true);
      const moduleItems = await getModules(id);
      const built = await Promise.all(
        moduleItems.map(async (mod) => {
          const topicItems = await getTopics(mod.id);
          // Topic model eager-loads 'contents' (LanguageLessons) via $with,
          // so we use topic.contents directly instead of extra per-topic API calls.
          const topicsWithLessons = topicItems.map((topic) => ({
            topic,
            lessons: Array.isArray(topic.contents) ? topic.contents : [],
          }));
          return { module: mod, topics: topicsWithLessons };
        }),
      );
      setTree(built);
      const expanded = {};
      built.forEach((b) => { expanded[b.module.id] = true; });
      setExpandedModules(expanded);
    } catch (err) {
      showError("Failed to load course structure.");
    } finally {
      setTreeLoading(false);
    }
  };

  // reload tree whenever we switch to the overview tab
  useEffect(() => {
    if (activeSection === "overview") loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const jumpTo = (section, moduleId, topicId) => {
    if (moduleId) setSelectedModuleId(moduleId);
    if (topicId)  setSelectedTopicId(topicId);
    setActiveSection(section);
    if (section === "topics" && moduleId)   refreshTopics(moduleId);
    if (section === "lessons" && moduleId)  refreshTopics(moduleId).then(() => { if (topicId) refreshLessons(topicId); });
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      setSaving(true); setError("");
      await deleteLanguageLesson(lessonId);
      if (selectedLessonId === lessonId) {
        setSelectedLessonId(null);
        setLessonDraft(EMPTY_LESSON);
      }
      await refreshLessons(selectedTopicId);
      flash("Lesson deleted.");
    } catch (err) {
      showError(err?.message || "Failed to delete lesson.");
    } finally { setSaving(false); }
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  if (loading) {
    return <div className="course-edit-page" style={{ padding: 40, textAlign: "center" }}>Loading course…</div>;
  }

  return (
    <div className="course-edit-page">

      {/* ── Header ── */}
      <div className="edit-header">
        <h1>Edit Course</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate("/courses")}>
            ← Back to Courses
          </button>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* ── Top Nav ── */}
      <div className="internal-nav">
        {[
          { key: "overview", label: "Overview",              icon: <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></> },
          { key: "course",   label: "Course Info",           icon: <path d="M3 7l9-4 9 4-9 4-9-4z" /> },
          { key: "modules",  label: "Modules",               icon: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></> },
          { key: "topics",   label: "Topics",                icon: <><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" /></> },
          { key: "lessons",  label: "Language Lessons",      icon: <><path d="M12 20h9" /><path d="M12 4h9" /><path d="M12 12h9" /><rect x="3" y="4" width="6" height="5" /><rect x="3" y="13" width="6" height="5" /></> },
        ].map(({ key, label, icon }) => (
          <button
            key={key}
            type="button"
            className={`internal-nav-btn ${activeSection === key ? "active" : ""}`}
            onClick={() => setActiveSection(key)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {icon}
            </svg>
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════
          0. OVERVIEW
      ══════════════════════════════════════════════ */}
      {activeSection === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "start" }}>

          {/* ── Left: module list ── */}
          <div className="edit-column" style={{ position: "sticky", top: 130 }}>
            <h2 style={{ marginBottom: 10 }}>Modules</h2>

            {treeLoading && <p style={{ color: "var(--muted)", fontSize: 13 }}>Loading…</p>}

            {!treeLoading && tree.length === 0 && (
              <div style={{ textAlign: "center", padding: "20px 0", color: "var(--muted)" }}>
                <p style={{ fontSize: 13 }}>No modules yet.</p>
                <button className="btn btn-outline btn-small" style={{ marginTop: 8 }} onClick={() => setActiveSection("modules")}>
                  + Add Module
                </button>
              </div>
            )}

            {!treeLoading && tree.map(({ module, topics: topicRows }, mi) => {
              const isActive = expandedModules.__selected === module.id;
              return (
                <div
                  key={module.id}
                  onClick={() => setExpandedModules((p) => ({ ...p, __selected: module.id }))}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    marginBottom: 6,
                    cursor: "pointer",
                    border: `1px solid ${isActive ? "var(--orange)" : "var(--border)"}`,
                    background: isActive ? "#fff7ef" : "#fff",
                    transition: "all .15s",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, color: isActive ? "var(--orange)" : "var(--text)" }}>
                    {mi + 1}. {module.title}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {topicRows.length} topic{topicRows.length !== 1 ? "s" : ""}
                    {" · "}
                    {topicRows.reduce((acc, t) => acc + t.lessons.length, 0)} lesson{topicRows.reduce((acc, t) => acc + t.lessons.length, 0) !== 1 ? "s" : ""}
                  </div>
                </div>
              );
            })}

            {!treeLoading && tree.length > 0 && (
              <button className="btn btn-outline btn-small w-full" style={{ marginTop: 6 }} onClick={() => setActiveSection("modules")}>
                + Add Module
              </button>
            )}
          </div>

          {/* ── Right: selected module detail ── */}
          <div className="edit-column">
            {(() => {
              const selectedEntry = tree.find((b) => b.module.id === expandedModules.__selected);
              if (treeLoading) return <p style={{ color: "var(--muted)" }}>Loading…</p>;
              if (!selectedEntry) return (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
                  <p>Select a module on the left to view its topics and lessons.</p>
                </div>
              );
              const { module, topics: topicRows } = selectedEntry;
              const moduleIndex = tree.findIndex((b) => b.module.id === module.id);
              return (
                <>
                  {/* Module header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <div>
                      <h2 style={{ margin: 0 }}>Module {moduleIndex + 1}: {module.title}</h2>
                      {module.introduction && (
                        <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0" }}>{module.introduction}</p>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button className="btn btn-outline btn-small" onClick={() => jumpTo("modules", module.id, null)}>
                        Edit Module
                      </button>
                      <button className="btn btn-outline btn-small" onClick={() => jumpTo("topics", module.id, null)}>
                        + Add Topic
                      </button>
                    </div>
                  </div>

                  {topicRows.length === 0 && (
                    <p style={{ fontSize: 14, color: "var(--muted)" }}>No topics yet. Add the first one above.</p>
                  )}

                  {topicRows.map(({ topic, lessons: topicLessons }, ti) => (
                    <div key={topic.id} style={{ marginBottom: 14, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>

                      {/* Topic header */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#f8f9fa", borderBottom: "1px solid var(--border)" }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
                            Topic {ti + 1}: {topic.title}
                          </span>
                          {topic.description && (
                            <p style={{ fontSize: 12, color: "var(--muted)", margin: "2px 0 0" }}>{topic.description}</p>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button className="btn btn-outline btn-small" onClick={() => jumpTo("topics", module.id, topic.id)}>
                            Edit Topic
                          </button>
                          <button className="btn btn-outline btn-small" onClick={() => jumpTo("lessons", module.id, topic.id)}>
                            + Lesson
                          </button>
                        </div>
                      </div>

                      {/* Lessons */}
                      <div style={{ padding: "10px 14px" }}>
                        {topicLessons.length === 0 ? (
                          <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>No lessons yet.</p>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {topicLessons.map((lesson) => (
                              <div
                                key={lesson.id}
                                style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}
                              >
                                {/* Lesson header bar */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", background: "#f8f9fa", borderBottom: "1px solid var(--border)" }}>
                                  <span style={{ background: "var(--orange)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 6 }}>
                                    {langName(lesson.language_id)}
                                  </span>
                                  <div style={{ display: "flex", gap: 6 }}>
                                    {lesson.topic_image && <span className="media-badge">🖼️ Image</span>}
                                    {lesson.audio       && <span className="media-badge">🔊 Audio</span>}
                                    {lesson.video       && <span className="media-badge">🎬 Video</span>}
                                    {lesson.document    && <span className="media-badge">📄 Document</span>}
                                    <button
                                      className="btn btn-outline btn-small"
                                      onClick={() => jumpTo("lessons", module.id, topic.id)}
                                    >
                                      Edit
                                    </button>
                                  </div>
                                </div>

                                {/* Lesson body — full text */}
                                <div style={{ padding: "10px 14px" }}>
                                  {lesson.topic_content ? (
                                    <p style={{ fontSize: 13, color: "var(--text)", margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                                      {lesson.topic_content}
                                    </p>
                                  ) : (
                                    <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, fontStyle: "italic" }}>No text content</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>

        </div>
      )}

      {/* ══════════════════════════════════════════════
          1. COURSE INFO
      ══════════════════════════════════════════════ */}
      {activeSection === "course" && (
        <div className="edit-content single-panel">
          <div className="edit-column">
            <h2>Course Details</h2>

            <div className="form-group">
              <label>Title *</label>
              <input
                className="form-input"
                value={courseDraft.title}
                onChange={(e) => setCourseDraft((p) => ({ ...p, title: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                rows={4}
                value={courseDraft.description}
                onChange={(e) => setCourseDraft((p) => ({ ...p, description: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Cover Image</label>
              <label className="icon-upload-btn">
                <input
                  className="hidden-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCourseDraft((p) => ({ ...p, image: e.target.files?.[0] || null }))}
                />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>{courseDraft.image?.name ? "Change Image" : "Upload Image"}</span>
              </label>
              {courseDraft.image?.name && (
                <p className="form-hint">Selected: {courseDraft.image.name}</p>
              )}
            </div>

            <div className="form-group">
              <label>Visibility</label>
              <div className="radio-group">
                {["private", "public"].map((v) => (
                  <label key={v} className="radio">
                    <input
                      type="radio"
                      name="visibility"
                      value={v}
                      checked={courseDraft.visibility === v}
                      onChange={(e) => setCourseDraft((p) => ({ ...p, visibility: e.target.value }))}
                    />
                    <span style={{ textTransform: "capitalize" }}>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <button className="btn btn-primary w-full" onClick={handleSaveCourse} disabled={saving}>
              {saving ? "Saving…" : "Save Course"}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          2. MODULES
      ══════════════════════════════════════════════ */}
      {activeSection === "modules" && (
        <div className="edit-content single-panel">
          <div className="edit-column">
            <h2>Modules</h2>

            {/* Add new module */}
            <p className="form-hint" style={{ marginBottom: 8 }}>Add a new module to this course.</p>
            <div className="form-group">
              <label>Module Title *</label>
              <input
                className="form-input"
                placeholder="e.g. Introduction to Nutrition"
                value={moduleDraft.title}
                onChange={(e) => setModuleDraft((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Introduction</label>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Brief introduction for this module"
                value={moduleDraft.introduction}
                onChange={(e) => setModuleDraft((p) => ({ ...p, introduction: e.target.value }))}
              />
            </div>
            <button className="btn btn-outline w-full" onClick={handleAddModule} disabled={saving}>
              + Add Module
            </button>

            {/* Module list */}
            {modules.length > 0 && (
              <>
                <hr className="col-divider" />
                <p className="form-hint" style={{ marginBottom: 8 }}>
                  Click a module to select it for editing below.
                </p>
                <div className="module-list">
                  {modules.map((m) => (
                    <div
                      key={m.id}
                      className={`module-item ${selectedModuleId === m.id ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedModuleId(m.id);
                        setModules((prev) => prev); // keep list, just select
                      }}
                    >
                      <span className="module-name">{m.title}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Edit / Delete selected module */}
            {selectedModule && (
              <>
                <hr className="col-divider" />
                <p style={{ fontWeight: 700, marginBottom: 8 }}>
                  Editing: <span style={{ color: "var(--orange)" }}>{selectedModule.title}</span>
                </p>
                <div className="form-group">
                  <label>Module Title</label>
                  <input
                    className="form-input"
                    value={selectedModule.title || ""}
                    onChange={(e) =>
                      setModules((prev) =>
                        prev.map((m) => m.id === selectedModule.id ? { ...m, title: e.target.value } : m)
                      )
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Introduction</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={selectedModule.introduction || ""}
                    onChange={(e) =>
                      setModules((prev) =>
                        prev.map((m) => m.id === selectedModule.id ? { ...m, introduction: e.target.value } : m)
                      )
                    }
                  />
                </div>
                <div className="form-row">
                  <button className="btn btn-primary" onClick={handleUpdateModule} disabled={saving}>
                    Save Changes
                  </button>
                  <button className="btn btn-danger" onClick={handleDeleteModule} disabled={saving}>
                    Delete Module
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          3. TOPICS
      ══════════════════════════════════════════════ */}
      {activeSection === "topics" && (
        <div className="edit-content single-panel">
          <div className="edit-column">
            <h2>Topics</h2>

            {/* Module selector */}
            <div className="form-group">
              <label>Select Module *</label>
              <select
                className="form-select"
                value={selectedModuleId || ""}
                onChange={(e) => handleModuleChange(e.target.value)}
              >
                <option value="">— Choose a module —</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>

            {!selectedModuleId ? (
              <p className="form-hint">Select a module above to manage its topics.</p>
            ) : (
              <>
                {/* Add topic */}
                <div className="form-group">
                  <label>Topic Title *</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Understanding Macronutrients"
                    value={topicDraft.title}
                    onChange={(e) => setTopicDraft((p) => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Topic Description</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    placeholder="Brief description (optional)"
                    value={topicDraft.description}
                    onChange={(e) => setTopicDraft((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <button className="btn btn-outline w-full" onClick={handleAddTopic} disabled={saving}>
                  + Add Topic
                </button>

                {/* Topic list */}
                {topics.length > 0 && (
                  <>
                    <hr className="col-divider" />
                    <p className="form-hint" style={{ marginBottom: 8 }}>
                      Click a topic to select it for editing.
                    </p>
                    <div className="topic-list">
                      {topics.map((t) => (
                        <div
                          key={t.id}
                          className="topic-card"
                          style={{
                            cursor: "pointer",
                            borderColor: selectedTopicId === t.id ? "var(--orange)" : undefined,
                            background: selectedTopicId === t.id ? "#fff9f4" : undefined,
                          }}
                          onClick={() => setSelectedTopicId(t.id)}
                        >
                          <strong>{t.title}</strong>
                          {t.description && (
                            <p style={{ fontSize: 12, color: "var(--muted)", margin: "4px 0 0" }}>{t.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Edit / Delete selected topic */}
                {selectedTopic && (
                  <>
                    <hr className="col-divider" />
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>
                      Editing: <span style={{ color: "var(--orange)" }}>{selectedTopic.title}</span>
                    </p>
                    <div className="form-group">
                      <label>Topic Title</label>
                      <input
                        className="form-input"
                        value={selectedTopic.title || ""}
                        onChange={(e) =>
                          setTopics((prev) =>
                            prev.map((t) => t.id === selectedTopic.id ? { ...t, title: e.target.value } : t)
                          )
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-textarea"
                        rows={2}
                        value={selectedTopic.description || ""}
                        onChange={(e) =>
                          setTopics((prev) =>
                            prev.map((t) => t.id === selectedTopic.id ? { ...t, description: e.target.value } : t)
                          )
                        }
                      />
                    </div>
                    <div className="form-row">
                      <button className="btn btn-primary" onClick={handleUpdateTopic} disabled={saving}>
                        Save Changes
                      </button>
                      <button className="btn btn-danger" onClick={handleDeleteTopic} disabled={saving}>
                        Delete Topic
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          4. LANGUAGE LESSONS
      ══════════════════════════════════════════════ */}
      {activeSection === "lessons" && (
        <div className="edit-content single-panel">
          <div className="edit-column">
            <h2>Language Lessons</h2>

            {/* Step 1: pick module */}
            <div className="form-group">
              <label>Step 1 — Select Module</label>
              <select
                className="form-select"
                value={selectedModuleId || ""}
                onChange={(e) => handleModuleChange(e.target.value)}
              >
                <option value="">— Choose a module —</option>
                {modules.map((m) => (
                  <option key={m.id} value={m.id}>{m.title}</option>
                ))}
              </select>
            </div>

            {/* Step 2: pick topic */}
            {selectedModuleId && (
              <div className="form-group">
                <label>Step 2 — Select Topic</label>
                <select
                  className="form-select"
                  value={selectedTopicId || ""}
                  onChange={(e) => handleTopicChange(e.target.value)}
                >
                  <option value="">— Choose a topic —</option>
                  {topics.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              </div>
            )}

            {!selectedTopicId ? (
              <p className="form-hint">Select a module and topic above to manage lessons.</p>
            ) : (
              <>
                <hr className="col-divider" />

                {/* ── Lesson form ── */}
                <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: "var(--orange)" }}>
                  {selectedLessonId ? "✏️ Editing Lesson" : "➕ Add New Lesson"}
                  {selectedTopic && (
                    <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 13, marginLeft: 6 }}>
                      for <strong>{selectedTopic.title}</strong>
                    </span>
                  )}
                </p>

                {/* Language selector */}
                <div className="form-group">
                  <label>Language *</label>
                  <select
                    className="form-select"
                    value={lessonDraft.language_id}
                    onChange={(e) => setLessonDraft((p) => ({ ...p, language_id: e.target.value }))}
                  >
                    <option value="">— Select language —</option>
                    {languages.map((l) => (
                      <option key={l.id} value={String(l.id)}>{l.name}</option>
                    ))}
                  </select>
                </div>

                {/* Text content */}
                <div className="form-group">
                  <label>Lesson Content</label>
                  <textarea
                    className="form-textarea"
                    rows={5}
                    placeholder="Type the lesson text here…"
                    value={lessonDraft.topic_content}
                    onChange={(e) => setLessonDraft((p) => ({ ...p, topic_content: e.target.value }))}
                  />
                </div>

                {/* Existing media (edit mode only) */}
                {selectedLessonId && lessonDraft._existing && (
                  <div className="form-group">
                    <label>Current Media Files</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 12px", background: "#f8f9fa", borderRadius: 8, border: "1px solid var(--border)" }}>
                      {lessonDraft._existing.image_url    && (
                        <a href={lessonDraft._existing.image_url}    target="_blank" rel="noreferrer" className="existing-file-link">🖼️ View current image</a>
                      )}
                      {lessonDraft._existing.audio_file   && (
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>🔊 Audio: {lessonDraft._existing.audio_file}</span>
                      )}
                      {lessonDraft._existing.video_url    && (
                        <a href={lessonDraft._existing.video_url}    target="_blank" rel="noreferrer" className="existing-file-link">🎬 View current video</a>
                      )}
                      {lessonDraft._existing.document_url && (
                        <a href={lessonDraft._existing.document_url} target="_blank" rel="noreferrer" className="existing-file-link">📄 View current document</a>
                      )}
                      {!lessonDraft._existing.image_url && !lessonDraft._existing.audio_file && !lessonDraft._existing.video_url && !lessonDraft._existing.document_url && (
                        <span style={{ fontSize: 13, color: "var(--muted)" }}>No media uploaded for this lesson.</span>
                      )}
                    </div>
                    <p className="form-hint">Upload new files below to replace any existing media.</p>
                  </div>
                )}

                {/* Media uploads */}
                <div className="form-group">
                  <label>Media Uploads {selectedLessonId ? "(upload to replace)" : "(optional)"}</label>
                  <div className="media-upload-grid">
                    <label className="icon-upload-btn">
                      <input className="hidden-input" type="file" accept="image/*"
                        onChange={(e) => setLessonDraft((p) => ({ ...p, topic_image: e.target.files?.[0] || null }))} />
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
                      </svg>
                      <span>Image</span>
                    </label>
                    <label className="icon-upload-btn">
                      <input className="hidden-input" type="file" accept="audio/*"
                        onChange={(e) => setLessonDraft((p) => ({ ...p, audio: e.target.files?.[0] || null }))} />
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 010 14.14" /><path d="M15.54 8.46a5 5 0 010 7.07" />
                      </svg>
                      <span>Audio</span>
                    </label>
                    <label className="icon-upload-btn">
                      <input className="hidden-input" type="file" accept="video/*"
                        onChange={(e) => setLessonDraft((p) => ({ ...p, video: e.target.files?.[0] || null }))} />
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" />
                      </svg>
                      <span>Video</span>
                    </label>
                    <label className="icon-upload-btn">
                      <input className="hidden-input" type="file"
                        onChange={(e) => setLessonDraft((p) => ({ ...p, document: e.target.files?.[0] || null }))} />
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span>Document</span>
                    </label>
                  </div>
                  <div className="selected-files-list">
                    {lessonDraft.topic_image?.name && <p className="form-hint">🖼️ {lessonDraft.topic_image.name}</p>}
                    {lessonDraft.audio?.name       && <p className="form-hint">🔊 {lessonDraft.audio.name}</p>}
                    {lessonDraft.video?.name       && <p className="form-hint">🎬 {lessonDraft.video.name}</p>}
                    {lessonDraft.document?.name    && <p className="form-hint">📄 {lessonDraft.document.name}</p>}
                  </div>
                </div>

                {/* Save / Cancel */}
                <div className="form-row">
                  <button className="btn btn-primary" onClick={handleSaveLesson} disabled={saving}>
                    {saving ? "Saving…" : selectedLessonId ? "Update Lesson" : "Add Lesson"}
                  </button>
                  {selectedLessonId && (
                    <button className="btn btn-ghost" onClick={handleCancelLesson} disabled={saving}>
                      Cancel Edit
                    </button>
                  )}
                </div>

                {/* ── Existing lessons list ── */}
                {lessons.length > 0 && (
                  <>
                    <hr className="col-divider" />
                    <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
                      Lessons for this topic ({lessons.length})
                    </p>
                    <div className="topic-list">
                      {lessons.map((lesson) => (
                        <div
                          key={lesson.id}
                          className="topic-card"
                          style={{
                            borderColor: selectedLessonId === lesson.id ? "var(--orange)" : undefined,
                            background:  selectedLessonId === lesson.id ? "#fff9f4" : undefined,
                          }}
                        >
                          {/* Language badge */}
                          <span style={{
                            display: "inline-block",
                            background: "var(--orange)",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "2px 10px",
                            borderRadius: 6,
                            marginBottom: 6,
                          }}>
                            {langName(lesson.language_id)}
                          </span>

                          {/* Content preview */}
                          <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {(lesson.topic_content || "").slice(0, 120) || <em>No text content</em>}
                          </p>

                          {/* Media indicators */}
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                            {lesson.topic_image && <span className="media-badge">🖼️ Image</span>}
                            {lesson.audio       && <span className="media-badge">🔊 Audio</span>}
                            {lesson.video       && <span className="media-badge">🎬 Video</span>}
                            {lesson.document    && <span className="media-badge">📄 Document</span>}
                          </div>

                          <div className="form-row">
                            <button className="btn btn-outline btn-small" disabled={saving}
                              onClick={() => handleEditLesson(lesson)}>
                              Edit
                            </button>
                            <button className="btn btn-danger btn-small" disabled={saving}
                              onClick={() => handleDeleteLesson(lesson.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {lessons.length === 0 && (
                  <p className="form-hint" style={{ marginTop: 12 }}>
                    No lessons yet for this topic. Add the first one above.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CourseEdit;
